import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import toast from "../helpers/toast";
import { logout } from "../components/pages/auth/authSlice";
import { createOfflineBaseQuery } from "./offlineBaseQuery";
import { getApiBaseUrl } from "../helpers/apiUrl";

const getNormalizedErrorMessage = (error) => {
  if (!error) return "Something went wrong";

  const genericBackendMessages = new Set([
    "Exception",
    "ValueError",
    "TypeError",
    "AttributeError",
    "RuntimeError",
    "Conflict",
  ]);

  let data = error?.data;

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = { message: data };
    }
  }

  const message = data?.message;
  const verbose = data?.verbose;

  if (message && !genericBackendMessages.has(message)) {
    return message;
  }

  return verbose || message || error?.error || error?.message || "Something went wrong";
};

const baseQuery = fetchBaseQuery({
  baseUrl: getApiBaseUrl(),
  //credentials: "include",
  prepareHeaders: (headers, { getState }) => {
    const access_token = getState().auth.access_token;
    if (access_token) {
      headers.set("authorization", `Bearer ${access_token}`);
    }
    return headers;
  },
});

const baseQuerywithAuth = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions);
  if (result?.error) {
    result.error.message = getNormalizedErrorMessage(result.error);

    // For queries (not mutations), auto-show the error — the component only
    // destructures `data` so it will never show anything on failure.
    // Mutations handle their own errors via unwrap() + catch.
    if (api.type === 'query' && result.error.status !== 401) {
      toast.error(result.error.message);
    }
  }
  if (result?.error?.status === 401) {
    // alert("Unauthorized");

    // unauthorized
    //exclude some endpoints from logout
    const excludedEndpoints = [
      "auth/login",
      "auth/refresh-token",
      "auth/logout",
      "auth/forgot-password",
      "auth/reset-password",
    ];

    //check if the endpoint is in the excluded endpoints
    const isExcluded = excludedEndpoints.some((endpoint) =>
      args?.url?.includes(endpoint)
    );

    if (isExcluded) {
      return result;
    }
    // Session expired – notify user and force logout
    toast.warning("Your session has expired. Please login again.");
    api.dispatch(logout());
    api.dispatch(apiSlice.util.resetApiState());
  }

  return result; //if everything is ok, return the result
};

// Wrap the authenticated base query with offline-first behaviour:
// - offline GETs are served from IndexedDB cache
// - offline mutations are queued in IndexedDB for later sync
// - successful online GETs populate the IndexedDB cache
const offlineAwareBaseQuery = createOfflineBaseQuery(baseQuerywithAuth);

export const apiSlice = createApi({
  reducerPath: "api",
  baseQuery: offlineAwareBaseQuery,
  endpoints: (builder) => ({}),
});
